import { useContext, useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	TextField,
	Typography,
	Paper,
	CircularProgress,
} from "@mui/material";
import {
	PermissionaryConfiguration,
	UpdatePermissionaryConfigurationDto,
} from "../../models/PermissionaryConfiguration";
import { AppContext } from "../../Contexts/AppContext";
import { Permissionary } from "../../models/Permissionary";
import PermissionaryConfigurationApi from "../../api/permissionary-configuration";

export default function PermissionaryConfigurationScreen() {
	const [config, setConfig] = useState<UpdatePermissionaryConfigurationDto>({
		_id: "",
		id: "",
		name: "",
		thresholdLastNotifiedAt: "",
		offlineThresholdSeconds: "",
		cabinetOpenThresholdSeconds: "",
		mainVoltageOffThresholdSeconds: "",
		reportConnectivityLastReceivedThresholdSeconds: "",
		deviceHistoryEnabled: false,
		// connectivityReportEnabled: false,
		lastConnectivityReportImported: "",
	});
	const [loading, setLoading] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	const { userContext, snackBar, setSnackBar } = useContext(AppContext);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value, type } = e.target;
		setConfig((prev) => ({
			...prev,
			[name]: type === "number" ? Number(value) : value,
		}));
	};

	const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setConfig((prev) => ({
			...prev,
			[name]: checked,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		console.log("Submitted config:", config);
		// TODO: send config to backend API

		try {
			setIsUpdating(true);

			console.log("config => ", config);

			await PermissionaryConfigurationApi.updateConfiguration(
				userContext?.permissionaryId as string,
				config,
				userContext?.accessToken as string
			);

			setSnackBar({
				open: true,
				message: "Configuracion actualizada",
				autoHideDuration: 6000,
				severity: "success",
				onClose: (event: React.SyntheticEvent | Event) => {
					setSnackBar({
						...snackBar,
						open: false,
					});
				},
			});
		} catch (error) {
			console.error("errors => ", error);
			setSnackBar({
				open: true,
				message: "Error actualizando configuracion",
				autoHideDuration: 6000,
				severity: "error",
				onClose: (event: React.SyntheticEvent | Event) => {
					setSnackBar({
						...snackBar,
						open: false,
					});
				},
			});
		} finally {
			setIsUpdating(false);
		}
	};

	const fetchPermissionaryConfiguration = async () => {
		if (!userContext?.permissionaryId) return;

		try {
			setLoading(true);
			const configuration: Partial<PermissionaryConfiguration> =
				await PermissionaryConfigurationApi.fetchConfiguration(
					userContext.permissionaryId,
					userContext.accessToken
				);

			setConfig(
				(prevConfig) =>
					({
						...configuration,
						thresholdLastNotifiedAt: configuration.thresholdLastNotifiedAt,
						offlineThresholdSeconds: configuration.offlineThresholdSeconds,
						cabinetOpenThresholdSeconds:
							configuration.cabinetOpenThresholdSeconds,
						mainVoltageOffThresholdSeconds:
							configuration.mainVoltageOffThresholdSeconds,
						reportConnectivityLastReceivedThresholdSeconds:
							configuration.reportConnectivityLastReceivedThresholdSeconds,
						deviceHistoryEnabled: configuration.deviceHistoryEnabled || false,
						lastConnectivityReportImported:
							configuration.lastConnectivityReportImported,
						//@ts-ignore
						name: configuration.name,
					} as PermissionaryConfiguration & { name: string })
			);
		} catch {
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (userContext) {
			fetchPermissionaryConfiguration();
		}
	}, [userContext]);

	return (
		<Paper sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
			<Typography textAlign={"center"} variant='h5' gutterBottom>
				Configuracion Permisionario.
			</Typography>

			<Box component='form' onSubmit={handleSubmit} noValidate>
				<Grid container spacing={2}>
					{!loading && (
						<>
							{/* Permissionary ID */}
							<Grid item xs={12}>
								<TextField
									fullWidth
									label='Permisionario'
									name='name'
									value={config.name || ""}
									onChange={handleChange}
								/>
							</Grid>

							{/* Thresholds */}
							<Grid item xs={6}>
								<TextField
									fullWidth
									label='Offline Threshold (seconds)'
									name='offlineThresholdSeconds'
									value={config.offlineThresholdSeconds}
									onChange={handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label='Cabinet Open Threshold (seconds)'
									name='cabinetOpenThresholdSeconds'
									value={config.cabinetOpenThresholdSeconds}
									onChange={handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label='Main Voltage Off Threshold (seconds)'
									name='mainVoltageOffThresholdSeconds'
									value={config.mainVoltageOffThresholdSeconds}
									onChange={handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label='Report Connectivity Last Received Threshold (seconds)'
									name='reportConnectivityLastReceivedThresholdSeconds'
									value={config.reportConnectivityLastReceivedThresholdSeconds}
									onChange={handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									fullWidth
									label='Threshold Last Notified At'
									name='thresholdLastNotifiedAt'
									value={config.thresholdLastNotifiedAt}
									onChange={handleChange}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									disabled
									fullWidth
									label='Last Connectivity Report Imported'
									name='lastConnectivityReportImported'
									value={config.lastConnectivityReportImported}
									onChange={handleChange}
								/>
							</Grid>

							{/* Toggles */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={config.deviceHistoryEnabled}
											onChange={handleCheckbox}
											name='deviceHistoryEnabled'
										/>
									}
									label='Device History Enabled'
								/>
							</Grid>
						</>
					)}
					{loading && (
						<Grid
							item
							sx={{
								textAlign: "center",
							}}
							xs={12}>
							<CircularProgress size={100} />
						</Grid>
					)}

					{/* Submit */}
					<Grid item xs={12}>
						<Button
							disabled={loading}
							type='submit'
							variant='contained'
							color='primary'
							fullWidth>
							{isUpdating && (
								<CircularProgress size={30} sx={{ color: "white" }} />
							)}
							{!isUpdating && <Typography>Guardar</Typography>}
						</Button>
					</Grid>
				</Grid>
			</Box>
		</Paper>
	);
}
